import type { FC, ReactNode } from "react"

import {
  UserCircleIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  UserPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"

import TailwindTextInput from "~/components/Tailwindcss/input"
import StateSelector from "~/components/Tailwindcss/StateSelector"

import type {
  LightReachApplicantRole,
  LightReachFormData,
  LightReachFormDisclosure,
} from "./lightreach-application"

/**
 * The LightReach application, as markup.
 *
 * Presentational on purpose: no proposal context, no Recoil, no mutation. The
 * seller's `LightReachForm` and the public link's `PublicLightReachForm` both
 * render this, which is the only reason a homeowner sees the same application
 * their consultant does — same fields, same disclosure text, same consent
 * boxes.
 *
 * What differs between the two is `identityReadOnly`. The seller may correct a
 * homeowner's phone number; a public link may not, because on that surface the
 * phone is the number LightReach would call about somebody else's credit
 * application. Everything else was already locked on both.
 */

export type LightReachApplicationFormProps = {
  formState: LightReachFormData
  disclosures: readonly LightReachFormDisclosure[]
  disclosuresLoading: boolean
  ssnError?: string
  coSsnError?: string
  submitError?: string | null
  /** Warnings above the form: eligibility gates the lender will not write. */
  banner?: ReactNode
  submitting: boolean
  canSubmit: boolean
  /** Locks the phone number too; name, email and address are always locked. */
  identityReadOnly?: boolean
  onFieldChange: (field: keyof LightReachFormData, value: string) => void
  onDisclosureChange: (
    role: LightReachApplicantRole,
    disclosureId: string,
    accepted: boolean,
  ) => void
  onSsnBlur: () => void
  onCoSsnBlur: () => void
  onAddCoApplicant: () => void
  onRemoveCoApplicant: () => void
  onSubmit: () => void
  onCancel: () => void
}

const DisclosureSkeleton: FC<{ rows: number }> = ({ rows }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }, (_, index) => (
      <div
        key={index}
        className="animate-pulse rounded-lg border border-gray-200 bg-white p-3"
      >
        <div className="flex items-start gap-3">
          <div className="mt-1 h-4 w-4 rounded bg-gray-200"></div>
          <div className="flex-1 space-y-2">
            <div className="h-3 w-3/4 rounded bg-gray-200"></div>
            <div className="h-2 w-full rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
)

const DisclosureList: FC<{
  acceptedIds: readonly string[]
  disclosures: readonly LightReachFormDisclosure[]
  idPrefix: string
  onChange: (disclosureId: string, accepted: boolean) => void
  textClassName: string
}> = ({ acceptedIds, disclosures, idPrefix, onChange, textClassName }) => {
  const accepted = new Set(acceptedIds)

  return (
    <div className="space-y-3">
      {disclosures.map((disclosure) => (
        <div
          key={disclosure.id}
          className="rounded-lg border border-gray-200 bg-white p-3"
        >
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id={`${idPrefix}-${disclosure.id}`}
              checked={accepted.has(disclosure.id)}
              onChange={(event) => onChange(disclosure.id, event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              required={disclosure.requireConsent}
            />
            <div className="flex-1">
              <label
                htmlFor={`${idPrefix}-${disclosure.id}`}
                className="block cursor-pointer text-sm font-medium text-gray-900"
              >
                {disclosure.name}
                {disclosure.requireConsent && (
                  <span className="ml-1 text-red-500">*</span>
                )}
              </label>
              <div className={textClassName}>{disclosure.text}</div>
              <div className="mt-1 text-xs text-gray-400">
                Type: {disclosure.type} | Version:{` `}
                {disclosure.version}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export const LightReachApplicationForm: FC<LightReachApplicationFormProps> = ({
  formState,
  disclosures,
  disclosuresLoading,
  ssnError,
  coSsnError,
  submitError,
  banner,
  submitting,
  canSubmit,
  identityReadOnly = false,
  onFieldChange,
  onDisclosureChange,
  onSsnBlur,
  onCoSsnBlur,
  onAddCoApplicant,
  onRemoveCoApplicant,
  onSubmit,
  onCancel,
}) => (
  <div className="flex max-h-[85vh] flex-col">
    {/* Header */}
    <div className="rounded-t-lg bg-gradient-to-r from-blue-500 to-blue-700 px-6 py-4">
      <div className="flex items-center gap-3">
        <h3 className="text-xl font-semibold text-white">
          LightReach Application
        </h3>
      </div>
      <p className="mt-1 text-sm text-white/80">
        Complete your application with LightReach
      </p>
    </div>

    {banner}

    {/* Error Display */}
    {submitError && (
      <div className="mx-6 mt-4 rounded border-l-4 border-red-400 bg-red-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{submitError}</p>
          </div>
        </div>
      </div>
    )}

    {/* Form Content */}
    <div className="flex-1 overflow-y-auto bg-white p-6">
      <form
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
        className="space-y-6"
      >
        {/* Personal Information Section */}
        <div>
          <h4 className="mb-4 flex items-center gap-2 text-lg font-medium text-gray-900">
            <UserCircleIcon className="h-5 w-5 text-blue-600" />
            Personal Information
          </h4>

          {/* Name */}
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TailwindTextInput
              label="First Name"
              value={formState.firstName}
              required={true}
              disabled={true}
              onChange={(value) => onFieldChange(`firstName`, value)}
            />
            <TailwindTextInput
              label="Last Name"
              value={formState.lastName}
              required={true}
              disabled={true}
              onChange={(value) => onFieldChange(`lastName`, value)}
            />
          </div>

          {/* Contact Information */}
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TailwindTextInput
              label="Email Address"
              type="email"
              value={formState.email}
              required={true}
              disabled={true}
              onChange={(value) => onFieldChange(`email`, value)}
            />
            <TailwindTextInput
              label="Phone Number"
              type="tel"
              value={formState.phoneNumber}
              required={true}
              disabled={identityReadOnly}
              onChange={(value) => onFieldChange(`phoneNumber`, value)}
            />
          </div>

          {/* SSN */}
          <div className="mb-4">
            <TailwindTextInput
              label="Social Security Number"
              type="password"
              value={formState.ssn}
              placeholder="XXXX (last 4) or full SSN"
              required={true}
              onChange={(value) => onFieldChange(`ssn`, value)}
              onBlur={onSsnBlur}
              error={ssnError}
              helperText="Enter last 4 digits or full SSN. Your information is encrypted and secure."
            />
          </div>
        </div>

        {/* Add Co-Applicant Button */}
        {!formState.showCoApplicant && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={onAddCoApplicant}
              className="flex items-center gap-2 rounded-md border-2 border-dashed border-blue-300 bg-blue-50 px-6 py-3 text-blue-700 transition-colors hover:border-blue-400 hover:bg-blue-100"
            >
              <UserPlusIcon className="h-5 w-5" />
              Add Co-Applicant
            </button>
          </div>
        )}

        {/* Co-Applicant Information Section */}
        {formState.showCoApplicant && (
          <div className="rounded-lg border-2 border-blue-200 bg-blue-50/30 p-4">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="flex items-center gap-2 text-lg font-medium text-gray-900">
                <UserCircleIcon className="h-5 w-5 text-blue-600" />
                Co-Applicant Information
              </h4>
              <button
                type="button"
                onClick={onRemoveCoApplicant}
                className="flex items-center gap-1 rounded-md px-3 py-1 text-sm text-red-600 transition-colors hover:bg-red-50"
              >
                <XMarkIcon className="h-4 w-4" />
                Remove
              </button>
            </div>

            {/* Name */}
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TailwindTextInput
                label="First Name"
                value={formState.coFirstName}
                required={true}
                onChange={(value) => onFieldChange(`coFirstName`, value)}
              />
              <TailwindTextInput
                label="Last Name"
                value={formState.coLastName}
                required={true}
                onChange={(value) => onFieldChange(`coLastName`, value)}
              />
            </div>

            {/* Contact Information */}
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TailwindTextInput
                label="Email Address"
                type="email"
                value={formState.coEmail}
                required={true}
                onChange={(value) => onFieldChange(`coEmail`, value)}
              />
              <TailwindTextInput
                label="Phone Number"
                type="tel"
                value={formState.coPhoneNumber}
                required={true}
                onChange={(value) => onFieldChange(`coPhoneNumber`, value)}
              />
            </div>

            {/* SSN */}
            <div className="mb-4">
              <TailwindTextInput
                label="Social Security Number"
                type="password"
                value={formState.coSsn}
                placeholder="XXXX (last 4) or full SSN"
                required={true}
                onChange={(value) => onFieldChange(`coSsn`, value)}
                onBlur={onCoSsnBlur}
                error={coSsnError}
                helperText="Enter last 4 digits or full SSN. Your information is encrypted and secure."
              />
            </div>

            {/* Co-Applicant Disclosures */}
            <div>
              <h5 className="mb-3 flex items-center gap-2 text-base font-medium text-gray-900">
                <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                Co-Applicant Disclosures & Agreements
              </h5>
              {disclosuresLoading ? (
                <DisclosureSkeleton rows={2} />
              ) : disclosures.length > 0 ? (
                <DisclosureList
                  acceptedIds={formState.coAcceptedDisclosureIds}
                  disclosures={disclosures}
                  idPrefix="co-disclosure"
                  onChange={(disclosureId, accepted) =>
                    onDisclosureChange(`coApplicant`, disclosureId, accepted)
                  }
                  textClassName="mt-1 max-h-24 overflow-y-auto text-xs text-gray-600"
                />
              ) : null}
            </div>
          </div>
        )}

        {/* Address Section */}
        <div>
          <h4 className="mb-4 flex items-center gap-2 text-lg font-medium text-gray-900">
            <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
            Address Information
          </h4>

          {/* Street Address */}
          <div className="mb-4">
            <TailwindTextInput
              label="Street Address"
              value={formState.address1}
              required={true}
              disabled={true}
              onChange={(value) => onFieldChange(`address1`, value)}
            />
          </div>

          {/* City, State, Zip */}
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-3">
              <TailwindTextInput
                label="City"
                value={formState.city}
                required={true}
                disabled={true}
                onChange={(value) => onFieldChange(`city`, value)}
              />
            </div>
            <div className="col-span-2">
              <StateSelector
                selectedState={formState.state}
                disabled={true}
                onStateChange={(stateCode) => onFieldChange(`state`, stateCode)}
              />
            </div>
            <div className="col-span-1">
              <TailwindTextInput
                label="ZIP Code"
                value={formState.zip}
                required={true}
                disabled={true}
                onChange={(value) => onFieldChange(`zip`, value)}
              />
            </div>
          </div>
        </div>

        {/* Disclosures Section */}
        <div>
          <h4 className="mb-4 flex items-center gap-2 text-lg font-medium text-gray-900">
            <DocumentTextIcon className="h-5 w-5 text-blue-600" />
            Disclosures & Agreements
          </h4>

          {disclosuresLoading ? (
            <div className="space-y-4">
              <DisclosureSkeleton rows={3} />
              <div className="mt-2 text-center text-gray-500">
                Loading disclosures...
              </div>
            </div>
          ) : disclosures.length > 0 ? (
            <DisclosureList
              acceptedIds={formState.acceptedDisclosureIds}
              disclosures={disclosures}
              idPrefix="disclosure"
              onChange={(disclosureId, accepted) =>
                onDisclosureChange(`applicant`, disclosureId, accepted)
              }
              textClassName="mt-2 max-h-32 overflow-y-auto text-sm text-gray-600"
            />
          ) : (
            <div className="py-8 text-center text-gray-500">
              No disclosures available
            </div>
          )}
        </div>
      </form>
    </div>

    {/* Footer */}
    <div className="flex justify-between border-t border-gray-200 bg-gray-50 px-6 py-4">
      <button
        onClick={onCancel}
        type="button"
        className="rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
      >
        Cancel
      </button>
      <button
        onClick={onSubmit}
        type="button"
        disabled={!canSubmit || disclosuresLoading || submitting}
        className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
      >
        {submitting && (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
        )}
        {submitting ? `Submitting...` : `Submit Application`}
      </button>
    </div>
  </div>
)
